import * as Sentry from "@sentry/react";
import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";

import { API } from "../constants/api";
import {
  buildApiErrorFingerprint,
  buildIgnoredApiErrorBreadcrumb,
  shouldIgnoreApiError,
} from "./apiErrorReporting";
import { matchExpectedBusinessResponse } from "./expectedBusinessResponses";

/**
 * Errores a los que el interceptor de respuesta YA les dejo su migaja de
 * "ignorado".
 *
 * Existe porque el mismo fallo llega a Sentry por dos puertas: este
 * interceptor y `QueryCache.onError`. Antes cada una escribia su propia
 * migaja, asi que una sola query cancelada con `retry: 1` dejaba cinco
 * entradas en un anillo que por defecto guarda cien. Ese anillo es la memoria
 * de "que paso justo antes" del siguiente error de la pagina, y gastarlo en
 * duplicados de un fallo que ni siquiera se captura lo vacia de historia util:
 * con cinco entradas por peticion solo caben ~20 peticiones de contexto.
 *
 * Significa "el interceptor YA dejo la migaja de este error que no se va a
 * capturar". Esa migaja es `api.ignored` para un aborto o un fallo de
 * transporte, y `api.error` —que lleva status, url, metodo y `errorCode`, o
 * sea mas contexto— para una respuesta de negocio esperada
 * (`expectedBusinessResponses.ts`). En los dos casos la otra puerta no debe
 * escribir una segunda.
 *
 * Se resuelve marcando el ERROR, no la ruta, porque el objeto de error es lo
 * unico que las dos puertas comparten: axios rechaza con la misma instancia
 * que react-query recibe despues. Asi el interceptor sigue cubriendo todo el
 * trafico de `AxiosInstance` (queries, mutaciones y llamadas sueltas) y el
 * cache de react-query sigue cubriendo lo que el interceptor no ve —las
 * queries que usan `axios` pelado, como el reporte de credito—, sin que nadie
 * escriba dos veces lo mismo.
 *
 * Vive aqui y no en `apiErrorReporting.ts` a proposito: ese modulo es de
 * reglas puras (misma entrada, misma salida) y esto es estado mutable del
 * interceptor. Es un `WeakSet` para no retener errores: la marca desaparece
 * con el propio error.
 */
const errorsWithIgnoredBreadcrumb = new WeakSet<object>();

/**
 * True cuando este interceptor ya escribio la migaja de ESTE error que no se
 * captura, para que la otra puerta a Sentry no la repita.
 */
export function hasIgnoredApiErrorBreadcrumb(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    errorsWithIgnoredBreadcrumb.has(error)
  );
}

export const AxiosInstance = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Add Authorization header conditionally
AxiosInstance.interceptors.request.use(
  (config) => {
    const userToken = Cookies.get("userToken");

    if (userToken) {
      config.headers.Authorization = `Token ${userToken}`;
    } else {
      delete config.headers.Authorization;
    }

    // Add breadcrumb for API request tracking
    Sentry.addBreadcrumb({
      category: "api",
      message: `${config.method?.toUpperCase()} ${config.url}`,
      level: "info",
      data: {
        method: config.method,
        url: config.url,
      },
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - Capture API errors to Sentry
AxiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase();

    // Skip aborted/cancelled requests and transport failures. They are not
    // captured, but they still leave a breadcrumb so a later error on the same
    // page keeps the context of what happened right before it. (For an abort
    // caused by a hard navigation there is no "later error": the page is gone.)
    // This is the ONLY place that writes that breadcrumb for traffic going
    // through this instance: the error is marked so `QueryCache.onError` does
    // not write a second copy of it (see `errorsWithIgnoredBreadcrumb`).
    if (shouldIgnoreApiError(error)) {
      Sentry.addBreadcrumb(buildIgnoredApiErrorBreadcrumb(error));
      if (typeof error === "object" && error !== null) {
        errorsWithIgnoredBreadcrumb.add(error);
      }
      return Promise.reject(error);
    }

    // Add breadcrumb for failed responses
    Sentry.addBreadcrumb({
      category: "api.error",
      message: `${status || "Network Error"} ${url}`,
      level: "error",
      data: {
        status,
        url,
        method,
        errorCode: (error.response?.data as { code?: string })?.code,
      },
    });

    // Don't capture 401/403 errors (expected for auth flow)
    if (status === 401 || status === 403) {
      Sentry.addBreadcrumb({
        category: "auth",
        message: `Authentication error: ${status}`,
        level: "warning",
        data: { status, url },
      });
      return Promise.reject(error);
    }

    // Respuestas de negocio ESPERADAS: el folio que ya tiene contrato, el
    // enlace publico de multireporte caducado, el CP que no existe, la
    // investigacion borrada, el NIP incorrecto. La tabla de
    // `expectedBusinessResponses.ts` las reconoce por metodo + ruta anclada +
    // status exacto + un discriminador del CUERPO (y, cuando el cuerpo no
    // distingue el flujo sano del bug, por la marca que el call site puso en la
    // config), asi que la variante-bug del MISMO endpoint —payload malformado,
    // otro `detail`, `Access denied.`, la variante `force=true`, el Refresh de
    // un contrato standalone— se sigue capturando.
    //
    // Va DESPUES del breadcrumb `api.error` a proposito: esa migaja ya lleva
    // status, url, metodo y `errorCode`, que es el contexto mas rico
    // disponible, y no cuesta una entrada extra del anillo. El error se marca
    // para que `QueryCache.onError` no escriba una segunda migaja del mismo
    // fallo (ver `errorsWithIgnoredBreadcrumb`).
    //
    // `matchExpectedBusinessResponse` no lanza nunca (try/catch interno con
    // default "capturar"): si lanzara, este `Promise.reject` pasaria a
    // rechazar con un TypeError en vez del AxiosError y el front dejaria de
    // encontrar `error.response.data.contract_id`.
    if (matchExpectedBusinessResponse(error)) {
      if (typeof error === "object" && error !== null) {
        errorsWithIgnoredBreadcrumb.add(error);
      }
      return Promise.reject(error);
    }

    // Capture API errors with enhanced context
    Sentry.withScope((scope) => {
      scope.setTag("api_error", "true");
      scope.setTag("http_status", status?.toString() || "network_error");

      scope.setContext("request", {
        url,
        method,
        baseURL: error.config?.baseURL,
      });

      scope.setContext("response", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data:
          typeof error.response?.data === "object"
            ? JSON.stringify(error.response?.data).substring(0, 500)
            : error.response?.data,
      });

      // Set error level based on status code
      if (status && status >= 500) {
        scope.setLevel("error");
      } else if (status && status >= 400) {
        scope.setLevel("warning");
      }

      // Group by endpoint shape: ids are normalised, query values are dropped
      // except for boolean flags, which keep their value because they switch
      // what the backend does and can only ever split an issue in two, and,
      // when there is no HTTP status, the normalised message keeps aborts,
      // timeouts and transport failures in separate issues.
      scope.setFingerprint(
        buildApiErrorFingerprint({
          method,
          url,
          status,
          message: error.message,
        }),
      );

      Sentry.captureException(error);
    });

    return Promise.reject(error);
  },
);
